export interface SubmissionTemplateData {
  id: string;
  category: string;
  sub_category: string;
  variant: string;
  form_type: string;
  status?: string;
  csv_id?: number;
  question_tree: FormTreeData[];
}

export interface SubmissionFormData {
  id: string;
  display_id: string;
  title: string;
  status:
    | 'draft'
    | 'submitted'
    | 'pre_review'
    | 'irb_review'
    | 'post_review'
    | 'withdrawn'
    | 'deleted'
    | 'complete';
  sub_status?: string | null;
  sub_status_label?: string | null;
  submission_template_id: string | null;
  /**
   * OV-1180: every sub-category this submission covers, primary first. A combined
   * Modification lists more than one; everything else lists exactly its own.
   */
  sub_categories?: string[];
  last_saved?: string | null;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  expiration_date?: string | null;
  complete_date?: string | null;
  // Navigation fields for UI logic
  editable: boolean;
  // Policy-based permission check - true if current user can edit this submission
  can_edit?: boolean;
  // Path to the details/overview page
  show_path: string;
  // Template category (e.g. "IRB", "Medical Device")
  type?: string;
  // Owner model type: 'Study' for StudySubmission, 'StudySite' for StudySiteSubmission
  submission_type?: 'Study' | 'StudySite' | 'Submission';
  // ID of the direct owner (Study or StudySite depending on submission_type)
  owner_id?: string;
  submission_role?:
    | 'submission_manager'
    | 'submission_viewer'
    | 'irb_non_affiliated_reviewer';
  question_tree?: FormTreeData[];
  pi_name?: string | null;
  protocol_number?: string | null;
  site_name?: string | null;
}

// Question contract:
// - kind selects the base field renderer and answer value shape.
// - validations carries backend-enforced answer rules plus optional UI hints.
// - validations.format adds text semantics such as email or phone.
// - meta is for presentation hints only.
// - mapped backend attributes are not part of this frontend shape.
export type QuestionKind =
  | 'text'
  | 'textarea'
  | 'number'
  | 'checkbox'
  | 'select_single'
  | 'select_multi'
  | 'date'
  | 'file'
  | 'upload';

export interface Option {
  label: string;
  value: string;
}

export interface ValidationRule {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  /** Semantic format for text and textarea questions; backend-enforced, with optional UI hints. */
  format?: 'email' | 'phone';
  minItems?: number;
  maxItems?: number;
  step?: number;
  minDate?: string;
  maxDate?: string;
}

export interface QuestionMeta {
  placeholder?: string;
  helpText?: string;
  checkboxLabel?: string;
  document_category?: string;
  allow_multiple?: boolean;
  /** URL a `{{...}}`-marked segment of `prompt_template` links to — see renderPromptText.tsx. */
  reference_document_url?: string;
  /** Answer Assist (Feature 1): who sees suggestions for this question. */
  value_scope?: 'none' | 'study' | 'study_site';
  /** Verify Answer (Feature 2): corpus the did-you-mean oracle checks. */
  verify_scope?:
    | 'off'
    | 'policy'
    | 'study'
    | 'study_site'
    | 'organization'
    | 'authenticated';
  /** Public Collections (Feature 3): public directory for canonical lookups. */
  public_source?: 'places' | 'npi' | null;
  /** Person data — server clamps verify to policy scope regardless of dial. */
  person_data?: boolean;
}

// Mirrors QuestionBehavior::SUPPORTED_DEPENDS_ON_OPS — the only operators the
// server stores. On a multi-select parent, '=' means "contains".
export type DependsOnOp = '=' | '!=';

export interface QuestionData {
  id: string;
  slug: string;
  kind: QuestionKind;
  /**
   * Always plain, display-ready text — the backend (QuestionBlueprint /
   * TemplateQuestionBlueprint) already strips any `{{...}}`
   * reference-document marker before sending this field, so it is always
   * safe to render directly. Do not add marker-stripping logic on the
   * frontend; if a new API response ever needs it, fix it at the source
   * (the Blueprint), not here.
   */
  prompt: string;
  /**
   * The raw prompt source, `{{...}}` marker intact. Only two things should
   * ever read this: `renderPromptText(prompt_template, meta)` (to turn the
   * marker into a live link — see renderPromptText.tsx) and the form
   * builder's prompt edit box (which must save the real marked-up source,
   * not the display copy). Everything else should use `prompt` above.
   */
  prompt_template?: string;
  required: boolean;
  depends_on?: {
    question_slug: string;
    op: DependsOnOp;
    value: string;
  } | null;
  options?: Option[];
  validations?: ValidationRule;
  meta?: QuestionMeta;
  disabled?: boolean;
  details?: boolean;
  details_label?: string;
  effective_details_label?: string;
  csv_id?: number;
  group_position?: number;
}

export function allowMultipleUploads(question: QuestionData): boolean {
  return question.meta?.allow_multiple === true;
}

// Use QuestionData for all question types - unified interface

export interface FormTreeData {
  id: string;
  label: string;
  meta: Record<string, unknown>;
  questions: QuestionData[];
  children: FormTreeData[];
}

// Form data types
export type FormFieldValue =
  | string
  | number
  | boolean
  | string[]
  | File
  | null
  | undefined;

export type FormData = Record<string, FormFieldValue>;

export type SectionStatus =
  | 'not_started'
  | 'in_progress'
  | 'complete'
  | 'optional';

export interface NavigationSection {
  id: string;
  label: string;
  status: SectionStatus;
  hasClarifications?: boolean;
  unresolvedClarificationCount?: number;
  totalClarificationCount?: number;
}

export type VisibilityChangeCallback = (
  sectionId: string,
  isVisible: boolean
) => void;
